import { FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { ALLOCATION_QUERY, EXECUTE_ACTION_MUTATION } from '../queries';
import { AllocationWithUpdated } from '../table/table';

interface StartOverProps {
  isOpen: boolean;
  currentRow: AllocationWithUpdated;
  onClose: () => void;
  updateLocalAllocation: (updatedAllocation: FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][number]) => void;
  startFlashRowAnimation: (allocationId: string) => void;
  operationWorkflowTransitionTaskSummariesRefetch: () => void;
}

const StartOver = ({
  isOpen,
  currentRow,
  onClose,
  updateLocalAllocation,
  startFlashRowAnimation,
  operationWorkflowTransitionTaskSummariesRefetch
}: StartOverProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resetAllocationWorkflowLoading, setResetAllocationWorkflowLoading] = useState(false);

  const [executeAction] = useMutation(EXECUTE_ACTION_MUTATION);

  const onSave = async () => {
    try {
      setErrorMessage(null);

      if (!currentRow.id) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      setResetAllocationWorkflowLoading(true);
      await executeAction({
        variables: {
          data: {
            allocationId: currentRow.id,
            action: 'startOver'
          }
        }
      });

      operationWorkflowTransitionTaskSummariesRefetch();
      startFlashRowAnimation(currentRow.id);
      onClose();
      updateAllocationRowData();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setResetAllocationWorkflowLoading(false);
    }
  };

  const updateAllocationRowData = async () => {
    try {
      const { data: allocationData } = await client.query({
        query: ALLOCATION_QUERY,
        variables: {
          id: currentRow.id
        }
      });

      updateLocalAllocation(allocationData.Allocation);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <ModalWrappedContent
      title="Start Over"
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!resetAllocationWorkflowLoading}
      showCancelButton={!resetAllocationWorkflowLoading}
      isUseOutsideClick={!resetAllocationWorkflowLoading}
      confirmButton={{ name: 'Confirm', onClick: onSave, disabled: resetAllocationWorkflowLoading }}
    >
      {resetAllocationWorkflowLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalBody>
            This action will void the Docusign envelope and restart this transaction with the Generate Signature Packet Wizard. Are you sure you wish
            to proceed?
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default StartOver;

const ModalBody = styled.div`
  text-align: center;
  line-height: 22.4px;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
